import { missionsApi } from "store/apis/missionsAPI/missions.api"
import { PROFESSION_TYPES } from "store/apis/professionsAPI/values"
import { balanceEndpoints } from "store/apis/balanceAPI/balance.api"
import { TAGS, api } from "../../api"
import {
  IExpedition,
  IExpeditionAttemptsResponse,
  IExpeditionProgress,
  IExpeditionWithBalanceResponse
} from "./types"

const expeditionsAPI = api.enhanceEndpoints({
  addTagTypes: [TAGS.EXPEDITIONS, TAGS.PROFESSIONS, TAGS.USER, TAGS.MISSIONS]
})

export const expeditionsEndpoints = expeditionsAPI.injectEndpoints({
  endpoints: (builder) => ({
    getExpeditionProgress: builder.query<IExpeditionProgress, void>({
      query: () => ({
        url: "/expeditions/progress",
        method: "GET"
      }),
      extraOptions: { maxRetries: 2 },
      providesTags: (result) => [
        {
          type: TAGS.EXPEDITIONS,
          id: (result?._id as string) || "EXPEDITION_PROGRESS"
        }
      ]
    }),
    getAllExpeditions: builder.query<IExpedition[], void>({
      query: () => ({
        url: "/expeditions",
        method: "GET"
      }),
      extraOptions: { maxRetries: 2 },
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ _id: id }) => ({
                type: TAGS.EXPEDITIONS as const,
                id
              })),
              { type: TAGS.EXPEDITIONS, id: "LIST" }
            ]
          : [{ type: TAGS.EXPEDITIONS, id: "LIST" }]
    }),
    startExpedition: builder.mutation<IExpeditionWithBalanceResponse, string>({
      query: (id) => ({
        url: `/expeditions/start/${id}`,
        method: "PATCH"
      }),
      onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
        const { data } = await queryFulfilled

        dispatch(
          expeditionsEndpoints.util.updateQueryData(
            "getExpeditionProgress",
            undefined,
            () => data.expedition
          )
        )
        dispatch(
          balanceEndpoints.util.updateQueryData(
            "getBalance",
            undefined,
            () => data.newBalance
          )
        )
        dispatch(
          missionsApi.util.invalidateTags([
            { type: TAGS.MISSIONS, id: "MY_PROGRESS" },
            { type: TAGS.MISSIONS, id: "HAS_UNCLAIMED" }
          ])
        )
      },
      extraOptions: { maxRetries: 2 }
    }),
    getExpeditionRewards: builder.mutation<IExpeditionProgress, void>({
      query: () => ({
        url: "/expeditions/get-reward",
        method: "PATCH"
      }),
      onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
        const { data } = await queryFulfilled

        dispatch(
          expeditionsEndpoints.util.updateQueryData(
            "getExpeditionProgress",
            undefined,
            () => data
          )
        )
      },
      extraOptions: { maxRetries: 2 }
    }),
    finishExpedition: builder.mutation<IExpeditionWithBalanceResponse, void>({
      query: () => ({
        url: "/expeditions/finish",
        method: "PATCH"
      }),
      extraOptions: { maxRetries: 2 },
      onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
        const { data } = await queryFulfilled

        dispatch(
          expeditionsEndpoints.util.updateQueryData(
            "getExpeditionProgress",
            undefined,
            () => data.expedition
          )
        )
        dispatch(
          balanceEndpoints.util.updateQueryData(
            "getBalance",
            undefined,
            () => data.newBalance
          )
        )
      },
      invalidatesTags: () => [
        { type: TAGS.PROFESSIONS, id: PROFESSION_TYPES.SCOUT },
        { type: TAGS.USER, id: `USER_GAME_STATISTICS` }
      ]
    }),
    getAttempts: builder.query<IExpeditionAttemptsResponse, void>({
      query: () => ({
        url: "/expeditions/attempts",
        method: "GET"
      }),
      onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
        const { data } = await queryFulfilled

        dispatch(
          expeditionsEndpoints.util.updateQueryData(
            "getExpeditionProgress",
            undefined,
            (draft: IExpeditionProgress) => {
              draft.attempts = data.attempts
              draft.nextRecovery = data.nextRecovery
            }
          )
        )
      },
      extraOptions: { maxRetries: 2 },
      providesTags: [{ type: TAGS.EXPEDITIONS, id: "ATTEMPTS" }]
    })
  })
})
